import { useCallback, useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { ChatMessage } from '@wundo/shared/chat.ts';
import { type ChatMessagePage, fetchChatMessages } from './chat.ts';

export interface ChatHistory {
  /** What was read back, oldest first. */
  messages: ChatMessage[];
  failed: boolean;
  /** Whether the conversation goes back further than what was read. */
  hasOlder: boolean;
  loadingOlder: boolean;
  loadOlder: () => void;
}

/**
 * A conversation read back, a page at a time: the most recent page first,
 * because that is the one being carried on, and older ones only if the reader
 * goes looking for them.
 */
export function useChatHistory(
  drawerOpen: boolean,
  conversationId: string | null,
): ChatHistory {
  const { data, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['chatMessages', conversationId],
      queryFn: async ({ pageParam }) =>
        await fetchChatMessages(conversationId ?? '', pageParam),
      initialPageParam: 1,
      getNextPageParam: (page: ChatMessagePage) => page.older,
      enabled: drawerOpen && conversationId !== null,
      /* Read again on every open: replies land while the drawer is away. */
      staleTime: 0,
    });

  /* Pages run newest to oldest; the drawer reads the other way. */
  const messages = useMemo(
    () => [...(data?.pages ?? [])].reverse().flatMap((page) => page.messages),
    [data],
  );

  const loadOlder = useCallback(() => void fetchNextPage(), [fetchNextPage]);

  return {
    messages,
    failed: isError,
    hasOlder: hasNextPage,
    loadingOlder: isFetchingNextPage,
    loadOlder,
  };
}
